import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material'
import { filter, get, isEmpty, isNil, map } from 'lodash'
import { useEffect, useState } from 'react'
import uuid from 'react-uuid'
import { ExperimentFiltersOptions } from '../../../api/models'
import CustomDialog from '../../../components/dialogs/CustomDialog/CustomDialog'
import { FormFieldLabel } from '../../../components/form/FormFieldLabel'
import { YEAR_BOUNDARY } from '../../../constants/experimentYearBoundary'
import styles from './FiltersDialog.module.css'
interface FiltersDialogProps {
  open: boolean
  primaryAction: (filters: object) => Promise<void>
  primaryActionButtonText: string
  secondaryAction: () => void
  secondaryActionButtonText: string
  title: string
  filters: object
  filterOptions: ExperimentFiltersOptions | null
  year?: number
}

const FiltersDialog = ({
  open,
  primaryAction,
  primaryActionButtonText,
  secondaryAction,
  secondaryActionButtonText,
  title,
  filters,
  filterOptions,
  year,
}: FiltersDialogProps) => {
  const [internalFilters, setInternalFilters] = useState<object>(filters)
  const isBefore2025 = year ? year <= YEAR_BOUNDARY : false
  const is2025 = year === YEAR_BOUNDARY + 1

  const priorityFilterField = isBefore2025 ? 'planning_basics.application_area' : 'planning_basics.mto_initiative'
  const priorityOptionsKey = isBefore2025 ? 'application_area' : is2025 ? 'mto_initiative' : 'gla_strategic_priority'

  useEffect(() => {
    setInternalFilters(filters)
  }, [filters])

  return (
    <CustomDialog
      open={open}
      primaryAction={async () => await primaryAction(internalFilters)}
      primaryActionButtonText={primaryActionButtonText}
      secondaryAction={() => {
        secondaryAction()
        setInternalFilters(filters)
      }}
      secondaryActionButtonText={secondaryActionButtonText}
      title={title}
      clearFilters={() => setInternalFilters({ 'planning_basics.year': get(filters, 'planning_basics.year') })}
      clearFiltersButtonText="Clear All"
      fullWidth>
      <div className={styles.filtersDialogContent}>
        <div>
          <FormFieldLabel text="Experiment Status" />
          <div className={styles.chipOptions}>
            <div className={styles.chipOptionsList}>
              <Chip
                label="Show All"
                className={
                  isNil(get(internalFilters, 'planning_basics.experiment_status'))
                    ? styles.chipOptionSelected
                    : styles.chipOption
                }
                onClick={() =>
                  setInternalFilters({ ...internalFilters, 'planning_basics.experiment_status': undefined })
                }
              />
              {map(filterOptions?.status, option => (
                <Chip
                  key={option}
                  label={option}
                  className={
                    get(internalFilters, 'planning_basics.experiment_status') === option
                      ? styles.chipOptionSelected
                      : styles.chipOption
                  }
                  onClick={() =>
                    setInternalFilters({ ...internalFilters, 'planning_basics.experiment_status': option })
                  }
                />
              ))}
            </div>
          </div>
        </div>
        <div>
          <FormFieldLabel text="E@S Experiment" />
          <div className={styles.chipOptions}>
            <div className={styles.chipOptionsList}>
              <Chip
                label="Show All"
                className={
                  isNil(get(internalFilters, 'planning_basics.is_e_at_s_experiment'))
                    ? styles.chipOptionSelected
                    : styles.chipOption
                }
                onClick={() =>
                  setInternalFilters({ ...internalFilters, 'planning_basics.is_e_at_s_experiment': undefined })
                }
              />

              <Chip
                key={'Yes'}
                label={'Yes'}
                className={
                  get(internalFilters, 'planning_basics.is_e_at_s_experiment') === true
                    ? styles.chipOptionSelected
                    : styles.chipOption
                }
                onClick={() => setInternalFilters({ ...internalFilters, 'planning_basics.is_e_at_s_experiment': true })}
              />
              <Chip
                key={'No'}
                label={'No'}
                className={
                  get(internalFilters, 'planning_basics.is_e_at_s_experiment') === false
                    ? styles.chipOptionSelected
                    : styles.chipOption
                }
                onClick={() =>
                  setInternalFilters({ ...internalFilters, 'planning_basics.is_e_at_s_experiment': false })
                }
              />
            </div>
          </div>
        </div>
        <div>
          <FormFieldLabel text="Phase" />
          <div className={styles.chipOptions}>
            <div className={styles.chipOptionsList}>
              <Chip
                label="Show All"
                className={
                  isNil(get(internalFilters, 'planning_basics.experiment_phase_high_level'))
                    ? styles.chipOptionSelected
                    : styles.chipOption
                }
                onClick={() =>
                  setInternalFilters({ ...internalFilters, 'planning_basics.experiment_phase_high_level': undefined })
                }
              />
              {map(filterOptions?.phase, option => (
                <Chip
                  key={option}
                  label={option}
                  className={
                    get(internalFilters, 'planning_basics.experiment_phase_high_level') === option
                      ? styles.chipOptionSelected
                      : styles.chipOption
                  }
                  onClick={() =>
                    setInternalFilters({ ...internalFilters, 'planning_basics.experiment_phase_high_level': option })
                  }
                />
              ))}
            </div>
          </div>
        </div>
        <div className={styles.selectOptionsContainer}>
          <div className={styles.selectOptionsItem}>
            <FormFieldLabel text="OU" />
            <FormControl
              fullWidth
              size="small">
              {isEmpty(get(internalFilters, 'planning_basics.ou')) && <InputLabel shrink={false}>Select</InputLabel>}
              <Select
                size="small"
                value={get(internalFilters, 'planning_basics.ou') ?? ''}
                onChange={(e: SelectChangeEvent) => {
                  setInternalFilters({
                    ...internalFilters,
                    'planning_basics.country': undefined,
                    'planning_basics.ou': e.target.value,
                  })
                }}>
                <MenuItem
                  key={'no-ou'}
                  value={undefined}>
                  All
                </MenuItem>
                {map(filterOptions?.ou, option => (
                  <MenuItem
                    key={option}
                    value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className={styles.selectOptionsItem}>
            <FormFieldLabel text="Country" />
            <FormControl
              fullWidth
              size="small">
              <Autocomplete
                size="small"
                id={uuid()}
                key={get(internalFilters, 'planning_basics.ou')}
                options={filter(
                  filterOptions?.country,
                  option =>
                    isEmpty(get(internalFilters, 'planning_basics.ou')) ||
                    option.ou === get(internalFilters, 'planning_basics.ou'),
                )}
                onChange={(e, value) => {
                  if (value) {
                    setInternalFilters({
                      ...internalFilters,
                      'planning_basics.country': value.name,
                      'planning_basics.ou': value.ou,
                    })
                  } else if (value === null) {
                    setInternalFilters({ ...internalFilters, 'planning_basics.country': undefined })
                  }
                }}
                value={{
                  name: get(internalFilters, 'planning_basics.country', ''),
                  ou: get(internalFilters, 'planning_basics.ou', ''),
                }}
                autoHighlight
                getOptionLabel={option => option.name ?? ''}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props
                  return (
                    <Box
                      key={key}
                      component="li"
                      {...optionProps}>
                      {option.name}
                    </Box>
                  )
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    sx={{
                      input: {
                        '&::placeholder': {
                          opacity: 0.7,
                        },
                      },
                    }}
                    variant="outlined"
                    label={null}
                    placeholder="Select"
                    slotProps={{
                      htmlInput: {
                        ...params.inputProps,
                        autoComplete: 'new-password',
                      },
                    }}
                  />
                )}
              />
            </FormControl>
          </div>
        </div>
        <div className={styles.selectOptionsContainer}>
          <div className={styles.selectOptionsItem}>
            <FormFieldLabel text="GLA Strategic Priority" />
            <FormControl
              fullWidth
              size="small">
              {isEmpty(get(internalFilters, priorityFilterField)) && <InputLabel shrink={false}>Select</InputLabel>}
              <Select
                size="small"
                value={get(internalFilters, priorityFilterField) ?? ''}
                onChange={(e: SelectChangeEvent) => {
                  setInternalFilters({
                    ...internalFilters,
                    [priorityFilterField]: e.target.value,
                  })
                }}>
                <MenuItem
                  key={`no-${priorityOptionsKey}`}
                  value={undefined}>
                  All
                </MenuItem>
                {map(filterOptions?.[priorityOptionsKey], option => (
                  <MenuItem
                    key={option}
                    value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className={styles.selectOptionsItem}>
            <FormFieldLabel text={isBefore2025 ? 'Brand' : 'Charter'} />
            <FormControl
              fullWidth
              size="small">
              {isEmpty(
                get(internalFilters, isBefore2025 ? 'planning_basics.brand' : 'planning_basics.global_charter'),
              ) && <InputLabel shrink={false}>Select</InputLabel>}
              <Select
                size="small"
                value={
                  get(internalFilters, isBefore2025 ? 'planning_basics.brand' : 'planning_basics.global_charter') ?? ''
                }
                onChange={(e: SelectChangeEvent) => {
                  setInternalFilters({
                    ...internalFilters,
                    [isBefore2025 ? 'planning_basics.brand' : 'planning_basics.global_charter']: e.target.value,
                  })
                }}>
                <MenuItem
                  key={isBefore2025 ? 'no-brand' : 'no-global-charter'}
                  value={undefined}>
                  All
                </MenuItem>
                {map(filterOptions?.[isBefore2025 ? 'brand' : 'global_charter'], option => (
                  <MenuItem
                    key={option}
                    value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
        <div className={styles.selectOptionsContainer}>
          <div className={styles.selectOptionsItem}>
            <FormFieldLabel text="Methodology" />
            <FormControl
              fullWidth
              size="small">
              {isEmpty(get(internalFilters, 'planning_basics.methodology')) && (
                <InputLabel shrink={false}>Select</InputLabel>
              )}
              <Select
                size="small"
                value={get(internalFilters, 'planning_basics.methodology') ?? ''}
                onChange={(e: SelectChangeEvent) => {
                  setInternalFilters({ ...internalFilters, 'planning_basics.methodology': e.target.value })
                }}>
                <MenuItem
                  key={'no-methodology'}
                  value={undefined}>
                  All
                </MenuItem>
                {map(filterOptions?.methodology, option => (
                  <MenuItem
                    key={option}
                    value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className={styles.selectOptionsItem}>
            <FormFieldLabel text="Platform" />
            <FormControl
              fullWidth
              size="small">
              {isEmpty(get(internalFilters, 'setup_details.platform_partner')) && (
                <InputLabel shrink={false}>Select</InputLabel>
              )}
              <Select
                size="small"
                value={get(internalFilters, 'setup_details.platform_partner') ?? ''}
                onChange={(e: SelectChangeEvent) => {
                  setInternalFilters({ ...internalFilters, 'setup_details.platform_partner': e.target.value })
                }}>
                <MenuItem
                  key={'no-platform-partner'}
                  value={undefined}>
                  All
                </MenuItem>
                {map(filterOptions?.platform, option => (
                  <MenuItem
                    key={option}
                    value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
      </div>
    </CustomDialog>
  )
}

export default FiltersDialog
